import React from "react";
import { categoryLabel, priorityLabel, statusLabel } from "../lib/labels";

// Enum option lists (kept in sync with the backend).
export const CATEGORIES = ["Bug", "Feature Request", "Crash", "UI Issue"];
export const PRIORITIES = ["Low", "Medium", "High", "Critical"];
// Chei, nu text pentru ochi: se afișează prin lib/labels.js. „Awaiting
// Confirmation" stă aici, în `status`, fiindcă e o stare a sistemului pe care
// nouă locuri o compară — nu o notă scrisă de cineva.
export const STATUSES = ["Open", "In Progress", "Responded", "Fix Deployed",
  "Awaiting Confirmation", "Resolved", "Closed", "Reopened"];

// Map each value to a CSS variable (defined in index.css) — no hardcoded hex.
export const CATEGORY_VAR = { "Bug": "--fb-bug", "Feature Request": "--fb-feature", "Crash": "--fb-crash", "UI Issue": "--fb-ui" };
export const PRIORITY_VAR = { Critical: "--fb-critical", High: "--fb-high", Medium: "--fb-medium", Low: "--fb-low" };
export const STATUS_VAR = { Open: "--fb-open", "In Progress": "--fb-progress", Responded: "--fb-responded", "Fix Deployed": "--fb-fixdeployed", "Awaiting Confirmation": "--agent-needs-review", Resolved: "--fb-resolved", Closed: "--fb-closed", Reopened: "--fb-reopened" };

export default function FeedbackBadge({ varName, label, className = "" }) {
  if (!varName) {
    return <span className={`inline-flex items-center whitespace-nowrap px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-sm bg-fg/10 text-fg/70 ${className}`}>{label}</span>;
  }
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-sm ${className}`}
      style={{ color: `rgb(var(${varName}))`, backgroundColor: `rgb(var(${varName}) / 0.15)` }}
    >{label}</span>
  );
}

// Cheia stării „așteaptă confirmarea unui om". Se compară cu `ticket.status`,
// niciodată cu `custom_status`: acela e text scris de cineva.
export const AWAITING_CONFIRMATION = "Awaiting Confirmation";

// Rangul unei stări în fluxul de lucru. Sortarea se făcea alfabetic pe textul
// afișat (`custom_status || status`): o traducere nouă schimba ordinea listei.
export const statusOrder = (value) => {
  const i = STATUSES.indexOf(value);
  return i === -1 ? STATUSES.length : i;
};

// A ticket still needing someone's attention: everything except the two
// terminal statuses. This is THE definition of "active" — the sidebar badge,
// the tracker header and Atena's listing all count this same set, so the
// three numbers on screen agree. Do not inline a second copy.
export const ACTIVE_STATUSES = new Set(
  STATUSES.filter((s) => s !== "Resolved" && s !== "Closed"));
export const AWAITING_VAR = "--agent-needs-review";
// `custom_status` e strict decorativ: nicio culoare proprie, fiindcă nicio
// valoare a lui nu mai înseamnă ceva pentru sistem.
export const CUSTOM_STATUS_VAR = {};

export const CategoryBadge = ({ value }) => <FeedbackBadge varName={CATEGORY_VAR[value]} label={categoryLabel(value)} />;
export const PriorityBadge = ({ value }) => <FeedbackBadge varName={PRIORITY_VAR[value]} label={priorityLabel(value)} />;

const splitCompoundStatus = (value) => {
  const label = String(value || "");
  if (!label || STATUS_VAR[label] || CUSTOM_STATUS_VAR[label]) return [label].filter(Boolean);

  const parts = [];
  let rest = label;
  const known = [...STATUSES].sort((a, b) => b.length - a.length);
  while (rest) {
    const next = known.find((status) => rest.startsWith(status));
    if (!next) return [label];
    parts.push(next);
    rest = rest.slice(next.length);
  }
  return parts.length > 1 ? parts : [label];
};

function StatusBadgeGroup({ statuses }) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" aria-label={statuses.map(statusLabel).join(", apoi ")}>
      {statuses.map((status, index) => (
        <React.Fragment key={`${status}-${index}`}>
          {index > 0 && <span className="sr-only">, apoi </span>}
          {index > 0 && <span aria-hidden="true" className="text-fg/30">→</span>}
          <FeedbackBadge varName={STATUS_VAR[status] || CUSTOM_STATUS_VAR[status]} label={statusLabel(status)} />
        </React.Fragment>
      ))}
    </span>
  );
}

export const StatusBadge = ({ value, custom }) => {
  const baseStatuses = splitCompoundStatus(value);
  if (custom) {
    const customStatuses = splitCompoundStatus(custom);
    if (CUSTOM_STATUS_VAR[custom] && !STATUS_VAR[custom]) {
      return <FeedbackBadge varName={CUSTOM_STATUS_VAR[custom]} label={statusLabel(custom)} />;
    }
    const statuses = [...baseStatuses, ...customStatuses]
      .filter(Boolean)
      .filter((status, index, arr) => arr.indexOf(status) === index);
    return statuses.length > 1
      ? <StatusBadgeGroup statuses={statuses} />
      : <FeedbackBadge varName={STATUS_VAR[statuses[0]] || CUSTOM_STATUS_VAR[statuses[0]]} label={statusLabel(statuses[0])} />;
  }
  if (baseStatuses.length <= 1) return <FeedbackBadge varName={STATUS_VAR[value]} label={statusLabel(value)} />;
  return <StatusBadgeGroup statuses={baseStatuses} />;
};
